import type { CountryConfig } from './types.js';
import { kzPublic } from './kz.public.js';

/**
 * KZ БИН (legal entities) and ИИН (individuals/ИП) are both 12 digits and share one
 * checksum, so a single validator covers both. Check digit (12th) = weighted sum mod 11:
 * weights [1..11]; if that yields 10, retry with [3,4,5,6,7,8,9,10,11,1,2]; a second 10
 * means the number could not have been issued. Ref: github.com/vonlab/kz-iin. We do NOT
 * validate the DDMMYY birth-date prefix — it over-rejects legitimately-issued BINs.
 */
const W1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const W2 = [3, 4, 5, 6, 7, 8, 9, 10, 11, 1, 2];

function isValidBinIin(id: string): boolean {
  if (!/^\d{12}$/.test(id)) return false;
  const d = id.split('').map(Number);
  const calc = (w: number[]): number => w.reduce((s, wi, i) => s + wi * d[i], 0) % 11;
  let c = calc(W1);
  if (c === 10) {
    c = calc(W2);
    if (c === 10) return false;
  }
  return c === d[11];
}

export const kzConfig: CountryConfig = {
  ...kzPublic,
  // goszakup + mitwork + skpharmacy + samruk (KZ-native; samruk = Самрук-Казына from the
  // zakup.gov.kz single window, system_id=3 only) plus the IFI adapters made KZ-ready by a
  // scrape_config country override alone (worldbank/isdb/ebrd/adb/aiib/undp/ungm — see
  // KZ_SOURCE_SEED; ungm carries Kazakhstan's UNGM country id 2396). The scheduler
  // intersects registered adapters against this list, so a key here must also have a
  // KZ tender_sources row (enabled) to actually harvest.
  sourceKeys: [
    'goszakup',
    'mitwork',
    'skpharmacy',
    'samruk',
    'worldbank',
    'isdb',
    'ebrd',
    'adb',
    'aiib',
    'undp',
    'ungm',
  ],
  // Direct contracts are told apart by the procurement METHOD the row names
  // (`tender_results.method`), not by a source: the goszakup contract registry
  // and the results protocols carry every method in one feed. «Из одного
  // источника» — «путем прямого заключения договора» and «по несостоявшимся
  // закупкам» (ref_trade_methods 6) alike — is a contract with one named
  // supplier, nobody could bid against it. A row with NO method is not counted
  // either: among the contract rows that carry both БИНs and a method, single
  // source is 736 of 1,342 (55%, KZ prod 2026-10-15), so an unknown method is a
  // coin toss, not a competition. Measured method mix: see
  // `docs/explanation/tender-detail-entitlements.md` § Kazakhstan.
  competition: {
    nonCompetitiveSources: [],
    nonCompetitiveMethodMarkers: ['одного источника'],
    unknownMethodIsCompetitive: false,
  },
  companyId: {
    label: 'БИН/ИИН',
    validate: isValidBinIin,
  },
  // Stub until KZ's КТРУ classifier + keyword data exist (own spec). Honest: it never
  // mis-classifies — it keeps an explicit code and otherwise declines. An empty /
  // whitespace-only code is "no code", not a code: fold it to null so the ingestion
  // normalizer never stores `''` as an ИКПУ (matches the UZ `resolveIkpu` `!rawCode`
  // guard, so both countries treat a blank source code the same way).
  classifier: {
    resolve: (code, _title) => (code && code.trim() ? code : null),
    classify: (_title) => null,
  },
  // Owner, 2026-10-15: a company page for every company with 5 or more won tenders.
  // 36 months = the depth of the goszakup contract history load
  // (`ingest-goszakup-contracts-history.ts`). No ACTIVE test: no KZ register says
  // ACTIVE (`gbd_ul` only marks LIQUIDATED), so it would index nobody.
  companyIndex: { minCompetitiveAwards: 5, windowMonths: 36, requireActiveStatus: false },
  // Tenge, from the live price distribution (KZ prod 2026-10-16, 1,169,754 priced
  // `tender_results`): p0.1 = 400 ₸, p1 = 3,400 ₸, median 154,000 ₸, p99.99 =
  // 6.3 млрд ₸, max 84.3 млрд ₸ (a turnkey construction contract). Below 100 ₸
  // sit 1,001 rows, 921 of them at exactly 1 ₸ — symbolic sums on services
  // («утилизация имущества» for 2 ₸, technical supervision for 3 ₸); from 100 ₸
  // up the rows are real cheap goods (an eraser 255 ₸, a bandage 725 ₸). The
  // Uzbek 1,000 so'm floor read as tenge would count those as impossible.
  // Ceiling 1 трлн ₸ (~$1.9 bn): ~12× the largest contract ever seen, 0 rows.
  awardPlausibility: { floor: 100, ceiling: 1_000_000_000_000 },
};
